import { useParams } from 'react-router-dom';
import { UUID } from '../../../types/shared/UUID';
import { useHistory } from 'react-router';
import { useCallback, useEffect } from 'react';
import { clientUrls } from '../../../ui-toolkit/rest/ClientUrlFactory';
import { useListSigningAppointmentsQuery } from '../../../API/order/signingAppointment/SigningAppointmentAPI';

/*returns an object with two members
 * isAppointmentSelected is a boolean. it says whether a valid signingAppointment
 * setAppointmentParam is used to navigate to and from a specific appointment by pushing its uuid to the URL
 * */
export const useSomeAppointmentFromParam = (): {
  isAppointmentSelected: boolean;
  setAppointmentParam: (appointmentId?: UUID) => void;
} => {
  const { orderId, appointmentId } = useParams<{ orderId: UUID; appointmentId?: UUID }>();
  const history = useHistory();
  const setAppointmentParam = useCallback(
    (apptId?: UUID): void => {
      if (apptId) {
        history.push(clientUrls.urlFor.orderSchedulingByAppointment(orderId, apptId));
      } else {
        history.push(clientUrls.urlFor.orderScheduling(orderId));
      }
    },
    [history, orderId]
  );
  const { isAppointmentSelected, isSuccess } = useListSigningAppointmentsQuery(
    { orderId },
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        isAppointmentSelected: (data ?? []).some((appointment) => appointment.id === appointmentId),
      }),
    }
  );
  useEffect(() => {
    if (appointmentId && isSuccess && !isAppointmentSelected) {
      setAppointmentParam();
    }
  }, [setAppointmentParam, isAppointmentSelected, appointmentId, isSuccess]);
  return { isAppointmentSelected, setAppointmentParam };
};

export const useSelectAppointmentFromPathParam = () => {
  const { orderId, appointmentId } = useParams<{ orderId: UUID; appointmentId?: UUID }>();

  const { appointmentFromPath, ...rest } = useListSigningAppointmentsQuery(
    { orderId },
    {
      selectFromResult: ({ data, ...rest }) => {
        const appointmentFromPath = (data ?? []).find((appointment) => appointment.id === appointmentId);
        return { appointmentFromPath, ...rest };
      },
    }
  );
  return { appointmentFromPath, ...rest };
};
